/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState, ReactElement } from "react";

import { useFieldArray, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import * as Yup from "yup";

import { Checkbox, Stack, StackItem } from "@fluentui/react";
import { yupResolver } from "@hookform/resolvers/yup";

import ComboBoxController from "Components/ReactHookForm/ComboBoxController/ComboBoxController";
import TextFieldController from "Components/ReactHookForm/TextFieldController/TextFieldController";
import { SidePanel } from "Components/SidePanel/SidePanel";
import { TechnologyAreaStatusEnum } from "Constants/ApplicationConstants";
import { APPLICATION_STATUS_DROPDOWN_OPTIONS } from "Constants/ApplicationStatusTypes";
import { OperationStatus } from "Constants/OperationStatus";
import {
  fetchApplicationFlagsBySelectedApplicationId,
  updateApplicationStatusesAndRemoveFlags,
  updateApplicationStatusesAndRemoveFlagsReset,
} from "Dataflows/ApplicationDetails/ApplicationDetailsAction";
import {
  getApplicationFlagsBySelectedApplicationId,
  getManageApplicationOperationStatus,
  manageApplicationErrorMessage,
} from "Dataflows/ApplicationDetails/ApplicationDetailsSelector";
import { IApplicationFormTechnologyFocusAreaStatusUpdateData } from "Interfaces/Application/IApplicationFormTechnologyFocusAreaStatusUpdateData";

import { IManageApplicationSidePanelProps } from "./IManageApplicationSidePanel";

export const ManageApplicationSidePanel = (
  props: IManageApplicationSidePanelProps
): ReactElement => {
  const { isPanelOpen, setIsPanelOpen, applicationFormId, applicationTechnologyFocusAreas } = props;
  const dispatch = useDispatch();
  const applicationFlags = useSelector(getApplicationFlagsBySelectedApplicationId);
  const manageApplicationOpStatus = useSelector(getManageApplicationOperationStatus);
  const manageApplicationError = useSelector(manageApplicationErrorMessage);
  const [shouldRemoveFlags, setShouldRemoveFlags] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      justification: "",
      applicationTechnologyFocusAreas: applicationTechnologyFocusAreas,
    },
    resolver: yupResolver(
      Yup.object().shape({
        justification: Yup.string().required("Justification is required").max(3900),
        applicationTechnologyFocusAreas: Yup.array().of(
          Yup.object().shape({
            technologyFocusAreaStatus: Yup.string().test(
              "Validate decision",
              "Cannot revert to Decision Pending.",
              function (value): boolean {
                return value !== TechnologyAreaStatusEnum.DecisionPending;
              }
            ),
          })
        ),
      })
    ),
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const save = () => {
    return handleSubmit((formData) => {
      // Format data for API endpoint request
      const tfaStatusUpdateData = (): IApplicationFormTechnologyFocusAreaStatusUpdateData[] => {
        return formData.applicationTechnologyFocusAreas.map((item) => {
          const temp = {
            technologyFocusAreaId: 0,
            currentTechnologyFocusAreaStatusName: "",
            newTechnologyFocusAreaStatusName: "",
          };
          temp.technologyFocusAreaId = item.technologyFocusAreaId;
          temp.currentTechnologyFocusAreaStatusName = applicationTechnologyFocusAreas.filter(
            (x) => x.technologyFocusAreaName === item.technologyFocusAreaName
          )[0].technologyFocusAreaStatus;
          temp.newTechnologyFocusAreaStatusName = formData.applicationTechnologyFocusAreas.filter(
            (tfa) => tfa.technologyFocusAreaId === item.technologyFocusAreaId
          )[0].technologyFocusAreaStatus;
          return temp;
        });
      };

      dispatch(
        updateApplicationStatusesAndRemoveFlags(
          applicationFormId,
          tfaStatusUpdateData(),
          shouldRemoveFlags,
          formData.justification
        )
      );
    });
  };

  const dismissPanel = (): void => {
    setIsPanelOpen(false);
    dispatch(updateApplicationStatusesAndRemoveFlagsReset());
    reset();
  };

  useEffect(() => {
    if (manageApplicationOpStatus === OperationStatus.Succeeded) {
      dismissPanel();
    }
  }, [manageApplicationOpStatus]);

  useEffect(() => {
    // If we don't have application flags in state, fetch them
    if (applicationFlags === undefined && applicationFormId) {
      dispatch(fetchApplicationFlagsBySelectedApplicationId(applicationFormId));
    }
  }, []);

  const { fields } = useFieldArray({ control, name: "applicationTechnologyFocusAreas" });

  return (
    <form onSubmit={save()}>
      <SidePanel
        isOpen={isPanelOpen}
        title="Update Application Status"
        saveButtonLabel="Save"
        error={manageApplicationError}
        disableActions={manageApplicationOpStatus === OperationStatus.Pending}
        onDismiss={dismissPanel}
        onSave={save()}
      >
        <Stack tokens={{ childrenGap: "1rem" }}>
          <Stack.Item>
            {fields.map((field, index) => {
              return (
                <StackItem key={field.id} styles={{ root: { paddingBottom: "10px" } }}>
                  <ComboBoxController
                    control={control}
                    errors={errors}
                    inputName={`applicationTechnologyFocusAreas[${index}].technologyFocusAreaStatus`}
                    isRequired={false}
                    labelDisplayName={field.technologyFocusAreaName}
                    options={APPLICATION_STATUS_DROPDOWN_OPTIONS}
                    isDisabled={manageApplicationOpStatus === OperationStatus.Pending}
                    defaultSelectedKey={
                      APPLICATION_STATUS_DROPDOWN_OPTIONS.filter(
                        (opt) => opt.text === field.technologyFocusAreaStatus
                      )[0].text
                    }
                  />
                </StackItem>
              );
            })}
          </Stack.Item>
          <StackItem>
            <Checkbox
              label="Remove Flags"
              checked={shouldRemoveFlags}
              onChange={() => setShouldRemoveFlags(!shouldRemoveFlags)}
              disabled={applicationFlags === undefined || applicationFlags.length === 0}
            />
          </StackItem>
          <StackItem>
            <TextFieldController
              inputName="justification"
              control={control}
              errors={errors}
              isRequired
              isMultiline
              hasTooltip
              labelDisplayName="Justification"
              toolTipDisplayText="Please explain further"
              characterCountLimit={3900}
            />
          </StackItem>
        </Stack>
      </SidePanel>
    </form>
  );
};
